import { useMemo } from 'react'
import {
  getHighestTeam,
  isDefined,
  isEmpty,
  isNotEmpty,
  isTeamId,
  Logger,
  toTeamId,
  type CompanyId,
  type TeamId,
  type TinyTeam,
  type UserId,
} from '@terros/common'
import { useIsManager } from '../user/isManager'
import { useProfile } from '../user/get'
import { getPermissionFromMap, usePermissionMap } from '../permissions/map'
import { useTeams } from './list'

const logger = new Logger('defaultTeam')
// logger.level = 'verbose'

export function useDefaultManagedTeam(): TinyTeam | undefined {
  const { user } = useProfile()
  const { isManager } = useIsManager()
  if (isManager) return getHighestTeam(user?.teams)
}

export function useDefaultViewAs(): CompanyId | TeamId | undefined {
  const profile = useProfile()
  const { data: teams } = useTeams()
  return useMemo(() => {
    const readPermissionMap = getPermissionFromMap(profile, 'event:read', teams)

    if (isEmpty(readPermissionMap)) return
    const companyId = profile.user?.companyId
    if (!companyId) return
    if (readPermissionMap.includes(companyId)) return companyId
    const teamIds = readPermissionMap.filter(isTeamId)
    if (isNotEmpty(teamIds)) {
      const manageTeam = teamIds.find((teamId) => profile.user?.teams?.manages.includes(teamId))
      if (manageTeam) return manageTeam
      const directTeam = teamIds.find((teamId) => profile.user?.teams?.directMemberOf.some((t) => t.teamId === teamId))
      if (directTeam) return directTeam
      return teamIds.at(0)
    }
  }, [profile, teams])
}

type UseDefaultTeamInput = {
  companyId?: CompanyId
  userId?: UserId
}

export function useDefaultTeam(input: UseDefaultTeamInput = {}): TeamId | undefined {
  const { companyId, userId } = input
  const { user, isAdmin } = useProfile(userId)
  const isManager = isNotEmpty(user?.teams?.manages)
  const primaryTeamId = user?.primaryTeam?.teamId
  const permissionTeamIds = useUserListTeams()
  const { data: teams } = useTeams()
  const activeTeams = useMemo(() => new Set(teams?.map(toTeamId)), [teams])

  logger.verbose('primaryTeamId', primaryTeamId)
  const activeManagedTeams = useMemo(
    () => user?.teams?.manages.filter((teamId) => activeTeams.has(teamId)),
    [user, activeTeams]
  )
  const activeDirectMemberOfTeams = useMemo(
    () => new Set(user?.teams?.directMemberOf.filter((team) => activeTeams.has(team.teamId)).map(toTeamId)),
    [user, activeTeams]
  )

  if (isDefined(companyId) && user?.companyId !== companyId) return
  if (isDefined(primaryTeamId) && activeDirectMemberOfTeams.has(primaryTeamId)) return primaryTeamId
  const teamsWithUserListPerm = activeManagedTeams?.filter((teamId) => permissionTeamIds.includes(teamId))
  if (isNotEmpty(teamsWithUserListPerm)) return teamsWithUserListPerm.at(0)
  if (isAdmin) return
  if (isManager && isNotEmpty(activeManagedTeams)) return activeManagedTeams.at(0)
  return Array.from(activeDirectMemberOfTeams)?.at(0)
}

const useUserListTeams = (): TeamId[] => {
  const entityIds = usePermissionMap({ permissionType: 'user:list' })
  return entityIds.filter(isTeamId)
}
